import React, { useEffect, useState } from "react";
import SiteChrome from "@/components/SiteChrome";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { usePlatformMessages } from "@/lib/usePlatformMessages";

export default function AdminLogin() {
  const { t } = useI18n();
  const { m } = usePlatformMessages();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@supertmatik.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Consume the one-shot "session expired" flag dropped by the axios
  // interceptor before it triggered the redirect to this page.
  //
  // We render an inline banner above the form (NOT a Sonner toast) — the
  // toaster mounts lazily and silently drops calls made before its first
  // render, which is exactly when our useEffect runs after a hard reload.
  useEffect(() => {
    if (sessionStorage.getItem("_session_expired") === "1") {
      sessionStorage.removeItem("_session_expired");
      setSessionExpired(true);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      login(data);
      toast.success(m("login_ok", null, "Login OK"));
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || m("login_error", null, t("login_error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteChrome>
    <div className="min-h-screen bg-slate-50 font-[Nunito]">
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900 mb-6">{t("nav_admin")} — {t("login")}</h1>
        {sessionExpired && (
          <div
            className="mb-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-900 shadow-sm"
            data-testid="session-expired-banner"
            role="alert"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-rose-500" />
            <div className="text-sm">
              <div className="font-semibold">A tua sessão expirou.</div>
              <div className="text-rose-700">Por favor, volta a entrar para continuar.</div>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-8 space-y-4 shadow-sm">
          <div>
            <Label>{t("email")}</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label>{t("password")}</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button data-testid="btn-admin-login" type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 rounded-lg">
            {t("login")}
          </Button>
        </form>
      </div>
    </div>

    </SiteChrome>
  );
}
